import { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import Lenis from '@studio-freight/lenis';
import { SiteContentProvider } from './context/SiteContentContext';
import ScrollToTop from './components/ScrollToTop';
import GiftCardPopup from './components/GiftCardPopup';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import QuoteEstimatorPage from './pages/QuoteEstimatorPage';
import FirstTimerZonePage from './pages/FirstTimerZonePage';
import HealingTrackerPage from './pages/HealingTrackerPage';
import GalleryPage from './pages/GalleryPage';
import ArtistDetailPage from './pages/ArtistDetailPage';
import ContactPage from './pages/ContactPage';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import CollaboratePage from './pages/CollaboratePage';
import GiftCardPage from './pages/GiftCardPage';
import './App.css';

function App() {
  // Set page title and override any external scripts - more aggressive approach
  useEffect(() => {
    const targetTitle = 'Vision Tattoo | Custom Tattoo Studio';
    
    // Set immediately
    document.title = targetTitle;
    
    // Override title more frequently to catch external script
    const titleInterval = setInterval(() => {
      if (document.title !== targetTitle) {
        document.title = targetTitle;
      }
    }, 100); // Check every 100ms
    
    // Also use MutationObserver to catch title changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target.nodeName === 'TITLE' && document.title !== targetTitle) {
          document.title = targetTitle;
        }
      });
    });
    
    const titleElement = document.querySelector('title');
    if (titleElement) {
      observer.observe(titleElement, { childList: true, characterData: true, subtree: true });
    }
    
    // Also observe head for title element changes
    observer.observe(document.head, { childList: true, subtree: true });
    
    return () => {
      clearInterval(titleInterval);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    window.lenis = lenis;

    return () => {
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  return (
    <SiteContentProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <GiftCardPopup />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/quote-estimator" element={<QuoteEstimatorPage />} />
            <Route path="/first-timer" element={<FirstTimerZonePage />} />
            <Route path="/healing-tracker" element={<HealingTrackerPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/artists/:artistId" element={<ArtistDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/collaborate" element={<CollaboratePage />} />
            <Route path="/gift-cards" element={<GiftCardPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </SiteContentProvider>
  );
}

export default App;
